import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Chip,
  Badge,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Button,
  Stack,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useInquiries, InquiryWithDetails } from '../../hooks/useApi';
import { useAuth } from '../../AuthContext';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArchiveIcon from '@mui/icons-material/Archive';

const InquiriesPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [tabValue, setTabValue] = useState<string>('open');
  const [includeArchived, setIncludeArchived] = useState<boolean>(false);

  // ユーザーがスタッフまたは管理者かどうかを判定
  const userClaims = currentUser ? (currentUser as any).reloadUserInfo?.customAttributes : null;
  const roles: string[] = userClaims ? JSON.parse(userClaims).roles || [] : [];
  const isStaffOrAdmin = roles.includes('staff') || roles.includes('admin');

  // 問い合わせリストを取得
  const { data: inquiries, isLoading, error } = useInquiries({
    status: tabValue === 'all' ? undefined : tabValue,
    include_archived: includeArchived,
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const handleInquiryClick = (inquiryId: string) => {
    navigate(`/inquiries/${inquiryId}`);
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'warning' => {
    switch (status) {
      case 'open':
        return 'primary';
      case 'resolved':
        return 'success';
      case 'archived':
        return 'default';
      default:
        return 'warning';
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'open':
        return '対応中';
      case 'resolved':
        return '解決済み';
      case 'archived':
        return 'アーカイブ';
      default:
        return status;
    }
  };

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'たった今';
    if (diffMins < 60) return `${diffMins}分前`;
    if (diffHours < 24) return `${diffHours}時間前`;
    if (diffDays < 7) return `${diffDays}日前`;

    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>問い合わせを読み込んでいます...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          問い合わせの読み込みに失敗しました: {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          問い合わせ一覧
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isStaffOrAdmin
            ? '店舗に届いた問い合わせを管理できます'
            : 'あなたが送信した問い合わせの一覧です'}
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="inquiry status tabs">
          <Tab label="対応中" value="open" />
          <Tab label="解決済み" value="resolved" />
          <Tab label="すべて" value="all" />
        </Tabs>
      </Box>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button
          variant={includeArchived ? 'contained' : 'outlined'}
          startIcon={<ArchiveIcon />}
          onClick={() => setIncludeArchived(!includeArchived)}
          size="small"
        >
          アーカイブを{includeArchived ? '非表示' : '表示'}
        </Button>
      </Stack>

      {!inquiries || inquiries.length === 0 ? (
        <Alert severity="info">
          {tabValue === 'open' && '対応中の問い合わせはありません'}
          {tabValue === 'resolved' && '解決済みの問い合わせはありません'}
          {tabValue === 'all' && '問い合わせがありません'}
        </Alert>
      ) : (
        <Stack spacing={2}>
          {inquiries.map((inquiry: InquiryWithDetails) => (
            <Card
              key={inquiry.id}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 3,
                  bgcolor: 'action.hover',
                },
                transition: 'all 0.2s',
              }}
              onClick={() => handleInquiryClick(inquiry.id)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="h6" component="div">
                        {inquiry.item_name}
                      </Typography>
                      <Chip
                        label={getStatusLabel(inquiry.status)}
                        color={getStatusColor(inquiry.status)}
                        size="small"
                      />
                      {inquiry.archived_at && (
                        <Chip
                          icon={<ArchiveIcon />}
                          label="アーカイブ"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      店舗: {inquiry.store_name}
                    </Typography>
                    {inquiry.last_message_preview && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mt: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {inquiry.last_message_preview}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, ml: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatDateTime(inquiry.last_message_at)}
                    </Typography>
                    {inquiry.unread_count > 0 && (
                      <Badge badgeContent={inquiry.unread_count} color="error">
                        <ChatBubbleOutlineIcon color="action" />
                      </Badge>
                    )}
                    {inquiry.status === 'resolved' && inquiry.unread_count === 0 && (
                      <CheckCircleOutlineIcon color="success" />
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Container>
  );
};

export default InquiriesPage;
